import { DIAGRAM_EMIT_EVENTS } from "../../../diagram/modules/DiagramEvents";
import { PathfindingHandler } from "./pathfinding/PathfindingHandler";
import { GridCell } from "../components/cell/grid-cell";
import { DiagramEventEmitter } from "../../../diagram/modules/DiagramEventEmitter";
import { ConnectingLineHandler } from "./ConnectingLineHandler";
import { LineSelection } from "./selection/LineSelection";
import { CellSelection } from "./selection/CellSelection";
import { GridConfig } from "./GridConfig";
import { API_CALLS_CONST } from "../../../diagram/config/apiCalls.config";
import { GridSizedViewUtils } from "../utils/GridSizeViewUtils";
import { PluginRetrievalUtils } from "../utils/PluginRetrievalUtils";
import { GridDrawingUtils } from "../utils/GridDrawingUtils";
import { ConnectorsUtils } from "../utils/ConnectorsUtils";
import { SelectionWrapper } from "./selection/SelectionWrapper";
import { AnchoredPluginsHandler } from "./AnchoredPluginsHandler";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description handles the api calls
 */
export class APIHandler {
    eventEmitter = null;

    connectingLineHandler = null;

    lineSelection = null;

    cellSelection = null;

    config = null;

    anchoredPlugins = null;

    selectionWrapper;

    labelHandler;

    generatedGrid;

    CellIndependentPluginHandler;

    fabricDiagramCtx;

    camera;

    connectorsUtils = new ConnectorsUtils();

    diagramContent = null; // diagram content from grid-view - "getDiagramData()"

    constructor(labelHandler, cellIndependentPluginHandler, fabricDiagramCtx, camera, canvasCtx) {
        this.labelHandler = labelHandler;
        this.cellIndependentPluginHandler = cellIndependentPluginHandler;
        this.selectionWrapper = new SelectionWrapper(getInstanceIDFromCanvasInstance(canvasCtx));
        this.eventEmitter = new DiagramEventEmitter(getInstanceIDFromCanvasInstance(canvasCtx));
        this.connectingLineHandler = new ConnectingLineHandler(getInstanceIDFromCanvasInstance(canvasCtx));
        this.lineSelection = new LineSelection(getInstanceIDFromCanvasInstance(canvasCtx));
        this.cellSelection = new CellSelection(getInstanceIDFromCanvasInstance(canvasCtx));
        this.config = new GridConfig(getInstanceIDFromCanvasInstance(canvasCtx));
        this.anchoredPlugins = new AnchoredPluginsHandler(getInstanceIDFromCanvasInstance(canvasCtx));
        this.selectionWrapper.setCellIndependentHandler(cellIndependentPluginHandler);
        this.anchoredPlugins.setCellIndependentHandler(cellIndependentPluginHandler);
        this.anchoredPlugins.setConnectingLineHandler(this.connectingLineHandler);
        this.fabricDiagramCtx = fabricDiagramCtx;
        this.camera = camera;
        this.canvasCtx = canvasCtx;
    }

    /**
     * @description - Api Handler. Dispatches the api call to the appropiate method
     * @param {String} action - Api name
     * @param {Object} payload - Api payload
     * @param {GridCell[][]} grid - generated grid matrix
     * @param {Object} diagramContent - getDiagramData() - from gridView
     */
    apiCall(action, payload, grid, viewInstance, diagramContent) {
        if (viewInstance.oneAtATime) return;
        this.generatedGrid = grid;
        this.diagramContent = diagramContent;
        switch (action) {
            case API_CALLS_CONST.ADD_CELL_INDEPENDENT_CONTAINER:
                this.addCellIndependentContainer(payload.pluginID, payload.layout, payload.state, payload.data);
                break;
            case API_CALLS_CONST.UPDATE_CELL_INDEPENDENT_CONTAINER:
                this.updateCellIndependentContainer(payload.containerUuid, payload.data);
                break;
            case API_CALLS_CONST.ADD_CELL_INDEPENDENT_CONTAINER_CHILD:
                return this.addCellIndependentContainerChild(payload.containerUuid, payload.childPluginID, payload.state, payload.data);
            case API_CALLS_CONST.UPDATE_CELL_INDEPENDENT_CONTAINER_CHILD:
                this.updateCellIndependentContainerChild(payload.containerUuid, payload.childPluginUUID, payload.data);
                break;
            case API_CALLS_CONST.REMOVE_CELL_INDEPENDENT_CONTAINER_CHILD:
                this.removeCellIndependentContainerChild(payload.containerUuid, payload.childPluginUUID);
                break;
            case API_CALLS_CONST.REMOVE_CELL_INDEPENDENT_CONTAINER:
                this.removeCellIndependentContainer(payload.containerUuid);
                break;
            case API_CALLS_CONST.REMOVE_ALL_CELL_INDEPENDENT_PLUGINS:
                this.removeAllCellIndependentPlugins();
                break;
            case API_CALLS_CONST.ADD_CONNECTOR:
                return this.addConnector(payload.from, payload.to, viewInstance);
            case API_CALLS_CONST.DELETE_CONNECTOR:
                this.deleteConnector(payload.uuid);
                break;
            case API_CALLS_CONST.UPDATE_CONNECTOR_LABEL:
                this.updateConnectorLabel(payload.connectorUUID, payload.label);
                break;
            case API_CALLS_CONST.DELETE_PLUGIN:
                this.deletePlugin(payload.uuid, viewInstance);
                break;
            case API_CALLS_CONST.UPDATE_PLUGIN_LABEL:
                this.updatePluginLabel(payload.pluginUuid, payload.label, payload.noEvent);
                break;
            case API_CALLS_CONST.SELECT_CONNECTOR:
                this.selectConnector(payload.connectorUuid, payload.emitEvent);
                break;
            case API_CALLS_CONST.SELECT_PLUGIN:
                this.selectPlugin(payload.pluginUuid, payload.isCellIndependent, grid, payload.triggerEvent);
                break;
            case API_CALLS_CONST.UPDATE_PLUGIN_STATE:
                this.updatePluginState(payload.state, payload.pluginUuid, payload.isCellIndependent, payload.isConnector);
                break;
            case API_CALLS_CONST.UPDATE_PLUGIN_DESIGN:
                this.updatePluginDesign(payload);
                break;
            case API_CALLS_CONST.ADD_ANCHORED_PLUGIN:
                return this.addAnchoredPlugin(payload.anchoredTo, payload.pluginID, payload.anchorType, payload.position, payload.offset, payload.data);
            case API_CALLS_CONST.UPDATE_ANCHORED_PLUGIN:
                return this.updateAnchoredPlugin(payload);
        }
    }

    /**
       * @description adds anchored plugin
       * @param { String } anchoredTo - Uuid of the plugin that the element is anchored to
       * @param { String } pluginID - The ID of the plugin that must be created as anchored element. Possible value: "ANCHORED_HEXAGON"
       * @param { String } anchorType - The type of the plugin that the element is anchored to. Possbile value: "CELL_BASED"
       * @param { String } position - Default Value: TOP_LEFT // Possible Values: "TOP_LEFT", "BOTTOM_LEFT", "CENTER_LEFT", "TOP_CENTER", "CENTER_CENTER", "BOTTOM_CENTER", "TOP_RIGHT", "CENTER_RIGHT", "BOTTOM_RIGHT",
       * @param { Object } offset - Object of type: {x:numericValue, y: numericValue}. Positiv values will move the element to the right, or bottom of the initial position
       * @param { Object } data - Object of type: {text:'5', fillStyle: desiredColor} that will be set inside the anchored element.
       * @returns { String } - the uuid of the newly added plugin
     */
    addAnchoredPlugin(anchoredTo, pluginID, anchorType, position, offset, data) {
        const pluginRetrieval = new PluginRetrievalUtils();
        const plugin = pluginRetrieval.getPluginFromDiagram(pluginID, this.fabricDiagramCtx);
        const Factory = plugin.plugin;
        if (!data) data = {};
        data.id = plugin.id;
        const target = {
            targetUuid: anchoredTo,
            type: anchorType,
            targetInstance: this.findPluginByUuid(this.anchorType, anchoredTo),
            position,
            offset,
            data
        };
        if (!target.targetInstance) {
            console.error("Did not find any elements with the UUID provided for the anchor element");
            return;
        }
        return this.anchoredPlugins.addPlugin(target, Factory, this.canvasCtx, this.generatedGrid);
    }

    /**
        * @description - Update plugin with the uuid "pluginUuid" anchored to the element with uuid "anchorPluginUuid"
        * @param {String} options.pluginUuid - The anchored plugin UUID to be updated
        * @param {String} options.anchorPluginUuid  - The uuid of the plugin that the anchored plugin is anchored to
        * @param {Object} options.options - An object with the properties: {position, offset, data}
        * @returns { void }
        */
    updateAnchoredPlugin(options) {
        this.anchoredPlugins.updatePluginStateAndData(options, this.generatedGrid);
    }

    /**
     * @description Finds plugin by the provided type and uuid
     */
    findPluginByUuid(type, uuid) {
        let pluginRetrieval = null;
        const isCellOrLine = type === "CELL_BASED" || type === "LINE";
        const isCellIndependent = type === "CELL_INDEP";
        if (isCellOrLine) pluginRetrieval = this.connectingLineHandler;
        if (isCellIndependent) pluginRetrieval = this.cellIndependentPluginHandler;
        if (!isCellOrLine && !isCellIndependent) pluginRetrieval = [this.connectingLineHandler, this.cellIndependentPluginHandler];
        const sources = (Array.isArray(pluginRetrieval) ? pluginRetrieval : [pluginRetrieval]);
        for (let i = 0; i < sources.length; i++) {
            const plugin = sources[i].getPlugin(uuid, true, true);
            if (plugin) {
                return plugin;
            }
        }
    }

    /**
     * @param {Object} state - the state to be used for updating the plugin
     * @param {String} pluginUuid - The anchored plugin UUID to be updated
     * @param {Boolean} isCellIndependent - indicates if is a cell independent plugin or the child of a cell indepenent plugin
     * @param {Boolean} isConnector - indicates if the pluginUuid is for a connector
     * @description Updates the state of the given pluginUuid with the provided state
     * @returns { void }
     */
    updatePluginState(state, pluginUuid, isCellIndependent, isConnector) {
        let plugin = null;
        if (isCellIndependent) {
            const uuid = this.cellIndependentPluginHandler.getPlugin(pluginUuid);
            if (uuid) {
                plugin = this.cellIndependentPluginHandler.plugins.get(uuid.parent).instance;
                if (uuid.child) {
                    plugin = plugin.getChild(uuid.child);
                }
            }
        } else if (isConnector) {
            const connector = this.connectingLineHandler.connectors.get(pluginUuid);
            if (!connector) {
                console.error(`Couldnt find connector with uuid${pluginUuid}`);
                return;
            }
            plugin = connector.instance;
            // connector.instance.update({}, state);
            // connector.instance.draw();
        } else {
            const cellUuid = this.connectingLineHandler.getCellWithContentUUID(pluginUuid);
            if (cellUuid) {
                plugin = this.connectingLineHandler.cells.get(cellUuid).ref;
            }
        }

        this.labelHandler.updateStateLabel(state, pluginUuid);
        if (!plugin) {
            console.error("PLUGIN NOT FOUND FOR UPDATING STATE!");
            return;
        }
        if (isConnector && state.hasOwnProperty("active")) {
            state.apiActive = state.active;
        }
        plugin.update({}, state);
        plugin.draw();
    }

    /**
     * @param {Object} data - An object with properties to update plugin design
     * @description Updates the design of the selected plugin with the provided data
     * @returns { void }
     */
    updatePluginDesign(data) {
        const selectedCell = this.cellSelection.getSelectedCellContent(this.generatedGrid);
        this.labelHandler.setAdditionalOptions(data);
        if (selectedCell) {
            selectedCell.draw();
        }
    }

    /**
     * @description removes the provided plugin
     * @param {*} selectedCell
     */
    removePlugin(selectedCell, viewInstance) {
        const multipleEmits = this.connectingLineHandler.deleteCellAndConnectors(selectedCell, this.generatedGrid, viewInstance);
        selectedCell.update({}, { selected: false });
        selectedCell.clearContent();
        selectedCell.draw();
        if (multipleEmits) {
            if (viewInstance) {
                const diagramData = viewInstance.getDiagramData();
                multipleEmits.forEach(emitData => {
                    emitData.payload.newState = diagramData;
                });
            }
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, multipleEmits, true);
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.MULTIPLE_ACTIONS_HAPPENED, multipleEmits, null, true);
        }
    }

    /**
    * @param {String} uuid - the uuid of the connector to delete
    * @description Deletes a connector with the provided UUID
    * @returns { void }
    */
    removeConnector(uuid) {
        const connector = this.connectingLineHandler.connectors.get(uuid);
        const toCell = this.connectingLineHandler.cells.get(connector.to);
        const toContent = toCell.ref.getContentInstance();
        const fromCell = this.connectingLineHandler.cells.get(connector.from);
        const fromContent = fromCell.ref.getContentInstance();
        const fromUUID = fromContent.uuid; const toUUID = toContent.uuid; const toID = toContent.data.id; const
            fromID = fromContent.data.id;
        this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_DELETED, {
            CONNECTOR_UUID: connector.instance.uuid, FROM_UUID: fromUUID, FROM_ID: fromID, TO_UUID: toUUID, TO_ID: toID
        });
        this.connectingLineHandler.deleteConnector(uuid);
        this.selectionWrapper.resetSpecific(["LINE"]);
        this.labelHandler.deleteConnectorsLine(uuid);
        this.labelHandler.redrawLabels(fromCell.layout, this.canvasCtx);
        this.labelHandler.redrawLabels(toCell.layout, this.canvasCtx);
        this.fabricDiagramCtx.diagramCamera.draw(true);
        // this.lineSelection.setSelectedLine(null);
    }

    /**
     * @description Sets new label data on changeDataSet api call
     * @param {*} data
     * @param {*} type - Plugin or connector
     */
    setNewLabelData(data, type) {
        const modelData = type === "pluginLabel" && data && data.content ? data.content : (data && data.connectors ? data.connectors : []);
        for (let i = 0; i < modelData.length; i++) {
            if (type === "pluginLabel" && modelData[i].hasOwnState) {
                continue;
            }
            this.labelHandler.addLoadedNewLabels(modelData[i], modelData[i].label, type);
        }
    }

    /**
     * @description updates connector label
     * @param {*} connectorUUID  - connector who's label needs updating
     * @param {*} label - value to be set
     */
    updateConnectorLabel(connectorUUID, label) {
        this.labelHandler.updateConnectorLabel(connectorUUID, label, this.generatedGrid);
    }

    /**
    * @description - Updates a plugins label
    * @param { String }  pluginUuid - The plugin uuid who's label will be updated
    * @param { String } label - the new label value
    * @param { Boolean } noEvent - indicates that no event should be emitted
    * @returns { void }
    */
    updatePluginLabel(pluginUuid, label, noEvent) {
        this.labelHandler.updatePluginLabelValue(pluginUuid, label, noEvent);
    }

    /**
     * @description marks the connector with the provided uuid as selected
     * @param {*} connectorUuid
     */
    selectConnector(connectorUuid, emitEvent) {
        this.selectionWrapper.setSelected("LINE", connectorUuid, { noEvent: emitEvent ? !emitEvent : true, labelHandler: this.labelHandler });
    }

    /**
     * @description Marks a plugin as selected and deselects any other element that might be currrently selected
     * @param { String } pluginUuid - The uuid of the element
     * @param { Boolean } isCellIndependent - A boolean that indicates if the specifiecd plugin uuid is for a cell indepenendent plugin
     */
    selectPlugin(pluginUuid, isCellIndependent, grid, triggerEvent) {
        if (!isCellIndependent && pluginUuid) {
            const cellUuid = this.connectorsUtils.getCellsFromContentUuids([pluginUuid], this.connectingLineHandler);
            const cell = this.connectingLineHandler.cells.get(cellUuid[pluginUuid]).ref;
            const cellPos = {
                row: Math.floor((cell.layout.y - this.config.viewPadding) / this.config.cellHeight),
                col: Math.floor((cell.layout.x - this.config.viewPadding) / this.config.cellWidth)
            };
            this.selectionWrapper.setSelected("CELL", cellPos, { noEvent: !triggerEvent, grid, labelHandler: this.labelHandler });
        } else if (pluginUuid) {
            const data = this.cellIndependentPluginHandler.getPlugin(pluginUuid);
            this.selectionWrapper.setSelected("CELL_INDEP", data, { labelHandler: this.labelHandler });
        } else {
            this.selectionWrapper.resetSpecific(["CELL", "LINE", "CELL_INDEP", "ANCHORED_ELEM"]);
        }
    }

    /**
         * @param {string} containerUuid - The uuid of the container
         * @param {object} data - A object containing the properties: {label(string)}.
         * @description Updates the data of a cell independent container
         * @returns { void }
         */
    updateCellIndependentContainer(containerUuid, data) {
        this.cellIndependentPluginHandler.updateContainerData(containerUuid, { ...data, oldState: this.diagramContent });
    }

    /**
       * @param {string} containerUuid - The uuid of the container
       * @param {string} childPluginID - The plugin ID to be used for child creation. Ex - DATA_MODEL_PROPERTY
       * @param {object} state - A object containing the properties: {selected}. If selected is provided and is true, the plugin will be selected upon creation
       * @param {object} data - A object containing the properties: {label(string)}. If label is not provided, the default will be used
       * @description Creates a child inside a container based on the provided params
       * @returns {String} - the uuid of the newly created pluign
       */
    addCellIndependentContainerChild(containerUuid, childPluginID, state, data) {
        const childData = {
            containerUuid, childPluginID, state, data
        };
        return this.cellIndependentPluginHandler.addChild(childData, this.fabricDiagramCtx, this.diagramContent);
    }

    /**
        * @param {string} containerUuid - The uuid of the container
        * @param {string} childPluginUUID - The uuid of the child
        * @param {object} data - A object containing the properties: {label(string)}.
        * @description Updates the label of a child inside a cell independent container
        * @returns { void }
        */
    updateCellIndependentContainerChild(containerUuid, childPluginUUID, data) {
        this.cellIndependentPluginHandler.updateChildData(containerUuid, childPluginUUID, { ...data, oldState: this.diagramContent });
    }

    /**
     * @param {string} containerUuid - The uuid of the container
     * @param {string} childPluginUUID - The uuid of the child
     * @description Removes a child plugin from a cell independent container
     * @returns { void }
     */
    removeCellIndependentContainerChild(containerUuid, childPluginUUID) {
        this.cellIndependentPluginHandler.removeChild(containerUuid, childPluginUUID, this.diagramContent);
    }

    /**
       * @param {string} containerUuid - The uuid of the container
       * @description Removes a cell independent container
       * @returns { void }
       */
    removeCellIndependentContainer(containerUuid) {
        this.cellIndependentPluginHandler.removeContainer(containerUuid, this.diagramContent);
    }

    /**
       * @description API call used to remove all cell independent pluginss
       * @returns { void }
       */
    removeAllCellIndependentPlugins() {
        this.cellIndependentPluginHandler.removeAllCellIndependentPlugins();
    }

    /**
        * @description Provide the UUID of the from and to plugins and it will create a connection between the two
        * @param {String} from
        * @param {String} to
        * @returns {String} returns the new connector UUID
        */
    addConnector(from, to, viewInstance) {
        const cells = this.connectorsUtils.getCellsFromContentUuids([from, to], this.connectingLineHandler);
        const fromCell = this.connectingLineHandler.cells.get(cells[from]);
        const toCell = this.connectingLineHandler.cells.get(cells[to]);
        if (!fromCell) {
            console.error("Canceling creation of connector. Couldnt't find FROM cell!");
            return;
        }
        if (!toCell) {
            console.error("Canceling creation of connector. Couldnt't find TO cell!");
            return;
        }
        const pathFindingHandler = new PathfindingHandler(this.generatedGrid, fromCell.ref, toCell.ref);
        const path = pathFindingHandler.search();
        // check to see if there already exists a connector between point a and point b

        // if there already is one, update instead of creating a new connector

        // return the update connector id
        const hasExistingPath = (function (connectors) {
            for (const [key, value] of connectors) {
                if (value.to === toCell.ref.uuid && value.from === fromCell.ref.uuid) {
                    return key;
                }
            }
        }(this.connectingLineHandler.connectors));
        if (hasExistingPath) {
            const oldPassthroughCells = this.connectingLineHandler.connectors.get(hasExistingPath).passesThrough;
            this.connectingLineHandler.updateConnectorPathV2(hasExistingPath, path, this.generatedGrid, false, false);
            // update previously passed through cells
            oldPassthroughCells.forEach(cellUuid => {
                const cell = this.connectingLineHandler.cells.get(cellUuid);
                if (cell) cell.ref.draw();
            });
            return hasExistingPath;
        }

        return this.connectingLineHandler.createConnection(path, false, cells[from], cells[to], this.generatedGrid, {}, this.labelHandler, false, viewInstance);
    }

    /**
     * @param {String} uuid - the uuid of the connector to delete
     * @description Deletes a connector with the provided UUID
     * @returns { void }
     */
    deleteConnector(uuid) {
        this.removeConnector(uuid);
    }

    /**
        * @description Deletes the plugin with the provided uuid
        * @param { String } uuid - The uuid of the plugin to delete
        * @returns { void }
        */
    deletePlugin(uuid, viewInstance) {
        const cellRef = this.connectingLineHandler.cells.get(uuid).ref;
        this.labelHandler.deleteLabel(uuid, this.labelHandler);
        this.removePlugin(cellRef, viewInstance);
    }

    /**
    * @description Create a cell independent container on the grid
    * @param {string} pluginID -A string representing the ID of the plugin. Ex: DATA_MODEL
    * @param {object} layout - A object containing the properties: {width,height,x,y}. If width,height is not provided the default is used, if x,y is not provided a place on the board is searched. If no place is found, a error event is emited.
    * @param {object} state - A object containing the properties: {selected}. If selected is provided and is true, the plugin will be selected upon creation
    * @param {object} data - A object containing the properties: {label(string), children(objectArray)}. If label is not provided, the default will be used
    * @return { void }
    */
    addCellIndependentContainer(pluginID, layout, state, data) {
        layout = layout || {};
        state = state || {};
        data = data || {};
        const gridDrawing = new GridDrawingUtils();
        const gridSizeUtils = new GridSizedViewUtils();
        const pluginRetrieval = new PluginRetrievalUtils();
        const plugin = pluginRetrieval.getPluginFromDiagram({ pluginId: pluginID }, this.fabricDiagramCtx);
        const pluginSize = { ...plugin.defaultSize, ...layout };
        const cell = gridSizeUtils.findEmptySpace(this.cellIndependentPluginHandler, pluginSize, this.generatedGrid);
        if (cell) {
            const notInViewport = gridDrawing.isElementInViewportV2(cell.layout, pluginSize, this.camera.getVisibleArea(), this.camera.scrollbars);
            this.cellIndependentPluginHandler.createPlugin(plugin.plugin, plugin.id, { x: cell.layout.x, y: cell.layout.y }, pluginSize, this.generatedGrid, this.canvasCtx, data, state, false, this.diagramContent);
            if (notInViewport) {
                const val = { left: cell.layout.x - this.config.cellWidth, top: cell.layout.y - this.config.cellHeight };
                this.camera.switchCameraFocus(val);
            }
        } else {
            // increasing grid. addCellIndependentContainer will be called from increaseGridSize
            this.increaseGridSize(pluginSize, pluginID, layout, state, data);
        }
    }

    /**
     * @description increases the grid size and world size with the provided pluginSize
     */
    increaseGridSize(pluginSize, pluginID, layout, state, data) {
        const columnsToAdd = Math.ceil((pluginSize.width + this.config.cellWidth) / this.config.cellWidth);
        for (let row = 0; row < this.generatedGrid.length; row++) {
            const currentY = (row * this.config.cellHeight) + this.config.viewPadding;
            for (let i = 0; i < columnsToAdd; i++) {
                const currentX = (this.generatedGrid[row].length * this.config.cellWidth) + this.config.viewPadding;
                const layout = {
                    x: currentX, y: currentY, width: this.config.cellWidth, height: this.config.cellHeight, radius: 0
                };
                const instance = new GridCell(layout, {}, this.canvasCtx, this.labelHandler);
                this.generatedGrid[row].push(instance);
                instance.draw();
            }
        }
        const toIncrease = columnsToAdd * this.config.cellWidth;
        const newWorldSize = { width: this.camera.WIDTH + toIncrease, height: this.camera.HEIGHT };
        this.camera.setWorldView(newWorldSize);
        this.addCellIndependentContainer(pluginID, layout, state, data);
    }
}
